import {
  getMeOptions,
  getMeOrganizationsOptions,
  User,
  UserOrg,
} from "@aura-frontend/omnibus-az-client";
import * as Sentry from "@sentry/react";
import { Session } from "@supabase/supabase-js";
import { useQuery } from "@tanstack/react-query";
import equal from "fast-deep-equal";
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import { LoadingScreen } from "@/components/auth/LoadingScreen";
import { supabaseClient } from "@/config/supabase";
import { queryClient } from "@/lib/queryClient";
import { useMsalMigrationModal } from "@/stores/msal-migration-modal";
import { clearMsalSession, hasStaleMsalSession } from "@/utils/msal-migration";

interface AppContextType {
  user: User | null;
  session: Session | null;
  isLoggedIn: boolean;
  isAuthenticated: boolean;
  orgId: string | null;
  changeOrgId: (newOrgId: string) => void;
  userOrganizations: UserOrg[];
  currentOrganization: UserOrg | undefined;
  logout: () => Promise<void>;
  isLoading: boolean;
  error: Error | null;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

interface AppContextProviderProps {
  children: ReactNode;
}

interface OrganizationState {
  orgId: string | null;
  setOrgId: (newOrgId: string) => void;
  clearOrgId: () => void;
}

const useCurrentOrganization = create<OrganizationState>()(
  persist(
    (set) => ({
      orgId: null,
      setOrgId: (newOrgId: string) => set({ orgId: newOrgId }),
      clearOrgId: () => set({ orgId: null }),
    }),
    {
      name: "organization-storage",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

interface UserState {
  user: User | null;
  setUser: (newUser: User) => void;
  clearUser: () => void;
}

const useUser = create<UserState>()(
  persist(
    (set) => ({
      user: null,
      setUser: (newUser: User) => set({ user: newUser }),
      clearUser: () => set({ user: null }),
    }),
    {
      name: "user-storage",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

interface SavedOrganizationsState {
  /** Supabase user id the list belongs to */
  userId: string | null;
  organizations: UserOrg[];
  save: (userId: string, organizations: UserOrg[]) => void;
  clear: () => void;
}

/**
 * The user's organizations from their last load. Org roles rarely change, so
 * pages render from these while /me/organizations refreshes instead of
 * waiting on it.
 */
const useSavedOrganizations = create<SavedOrganizationsState>()(
  persist(
    (set) => ({
      userId: null,
      organizations: [],
      save: (userId, organizations) => set({ userId, organizations }),
      clear: () => set({ userId: null, organizations: [] }),
    }),
    {
      name: "user-organizations-storage",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

/**
 * The saved organizations to render from, if usable. A list without org roles
 * never is: that user is on No Access waiting to claim an invitation, maybe in
 * another tab or device, so they always wait on a fresh list.
 */
export const getUsableSavedOrganizations = (
  saved: Pick<SavedOrganizationsState, "userId" | "organizations">,
  userId: string | null,
) =>
  userId &&
  saved.userId === userId &&
  saved.organizations.some((org) => org.orgRoles.length > 0)
    ? saved.organizations
    : undefined;

export function AppContextProvider({ children }: AppContextProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isInitializingAuth, setIsInitializingAuth] = useState(true);
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);

  // Initialize Supabase session and listen for changes
  useEffect(() => {
    const initializeAuth = async () => {
      // Check Supabase session
      const { data } = await supabaseClient.auth.getSession();
      const hasSupabaseSession = !!data?.session;

      setSession(data?.session ?? null);
      setIsAuthenticated(hasSupabaseSession);

      // Check for stale MSAL session and handle migration
      const staleSessionDetected = hasStaleMsalSession();
      if (staleSessionDetected && !hasSupabaseSession) {
        // Stale MSAL session without Supabase auth: show migration modal
        useMsalMigrationModal.getState().open();
      } else if (hasSupabaseSession && staleSessionDetected) {
        // User already authenticated via Supabase: silently clear MSAL cache
        clearMsalSession();
      }

      setIsInitializingAuth(false);
    };

    initializeAuth();

    // Subscribe to Supabase auth state changes
    const {
      data: { subscription },
    } = supabaseClient.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession ?? null);
      setIsAuthenticated(!!newSession);

      // Clear stale MSAL session if user successfully authenticated via Supabase
      if (newSession && hasStaleMsalSession()) {
        clearMsalSession();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const logout = useMemo(
    () => async () => {
      try {
        setShowLoadingScreen(true);
        await supabaseClient.auth.signOut();

        // Clear React Query cache to ensure no stale data from previous user
        queryClient.removeQueries(getMeOptions());
        queryClient.removeQueries(getMeOrganizationsOptions());
        useSavedOrganizations.getState().clear();

        // Clear all remaining cache as a safety measure
        queryClient.clear();
      } catch (error) {
        console.error("Error during logout:", error);
        Sentry.captureException(error, {
          tags: { component: "app-context", event: "logout_error" },
        });
      } finally {
        setShowLoadingScreen(false);
      }
    },
    [],
  );

  const { isLoading: isLoadingMe, error: meError } = useQuery({
    ...getMeOptions(),
    select: (data) => {
      if (!equal(useUser.getState().user, data)) {
        useUser.getState().setUser(data);
      }
      return data;
    },
    enabled: isAuthenticated,
  });

  const {
    data: fetchedOrganizations,
    isLoading: isLoadingFetchedOrganizations,
    error: fetchedOrgError,
  } = useQuery({
    ...getMeOrganizationsOptions(),
    select: (data) => data.userOrganizations,
    enabled: isAuthenticated,
  });

  const sessionUserId = session?.user.id ?? null;
  const savedOrgUserId = useSavedOrganizations((state) => state.userId);
  const savedOrganizations = useSavedOrganizations(
    (state) => state.organizations,
  );
  const saveOrganizations = useSavedOrganizations((state) => state.save);
  const clearSavedOrganizations = useSavedOrganizations((state) => state.clear);

  useEffect(() => {
    if (fetchedOrganizations && sessionUserId) {
      saveOrganizations(sessionUserId, fetchedOrganizations);
    }
  }, [fetchedOrganizations, sessionUserId, saveOrganizations]);

  const fallbackOrganizations = useMemo(
    () =>
      getUsableSavedOrganizations(
        { userId: savedOrgUserId, organizations: savedOrganizations },
        sessionUserId,
      ),
    [savedOrgUserId, savedOrganizations, sessionUserId],
  );

  // Until the fetch lands, and if it fails, the saved list stands in
  const user_organizations = fetchedOrganizations ?? fallbackOrganizations;
  const isLoadingOrganizations =
    isLoadingFetchedOrganizations && !fallbackOrganizations;
  const orgError = user_organizations ? null : fetchedOrgError;

  const defaultOrgId = user_organizations?.[0]?.orgId ?? null;
  const { orgId, setOrgId, clearOrgId } = useCurrentOrganization();
  const { user, clearUser } = useUser();

  const currentOrganization = useMemo(() => {
    const found = user_organizations?.find((org) => org.orgId === orgId);
    return found;
  }, [user_organizations, orgId]);

  const isLoading = useMemo(() => {
    return (
      isInitializingAuth || (!user && isLoadingMe) || isLoadingOrganizations
    );
  }, [isInitializingAuth, isLoadingMe, isLoadingOrganizations, user]);

  useEffect(() => {
    if (!orgId && defaultOrgId) {
      setOrgId(defaultOrgId);
    }
  }, [defaultOrgId, orgId, setOrgId]);

  // Clear stale orgId if user is no longer part of that organization
  useEffect(() => {
    if (user_organizations && orgId) {
      const orgExists = user_organizations.some((org) => org.orgId === orgId);
      if (!orgExists) {
        clearOrgId();
        if (defaultOrgId) {
          setOrgId(defaultOrgId);
        }
      }
    }
  }, [user_organizations, orgId, defaultOrgId, setOrgId, clearOrgId]);

  useEffect(() => {
    // Only once auth has settled: the first render is always unauthenticated,
    // and clearing then would throw away the saved user and org every load
    if (!isInitializingAuth && !isAuthenticated) {
      clearUser();
      clearOrgId();
      clearSavedOrganizations();
    }
  }, [
    isAuthenticated,
    isInitializingAuth,
    clearUser,
    clearOrgId,
    clearSavedOrganizations,
  ]);

  // Clear orgId when user changes to prevent stale org references
  const previousUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      user?.userId &&
      previousUserIdRef.current &&
      previousUserIdRef.current !== user.userId
    ) {
      clearOrgId();
    }
    if (user?.userId) {
      previousUserIdRef.current = user.userId;
    }
  }, [user?.userId, clearOrgId]);

  useEffect(() => {
    if (session?.user) {
      Sentry.setUser({
        id: session.user.id,
        email: session.user.email,
      });
    } else if (!isAuthenticated) {
      Sentry.setUser(null);
    }
  }, [session, isAuthenticated]);

  const value = useMemo<AppContextType>(
    () => ({
      user,
      isLoggedIn: isAuthenticated,
      session,
      isAuthenticated,
      orgId,
      currentOrganization: currentOrganization ?? undefined,
      changeOrgId: setOrgId,
      userOrganizations: user_organizations ?? [],
      logout,
      isLoading,
      // A saved user stands in if /me fails, like saved organizations
      error: (user ? null : meError) || orgError,
    }),
    [
      user,
      isAuthenticated,
      session,
      orgId,
      currentOrganization,
      setOrgId,
      user_organizations,
      logout,
      isLoading,
      meError,
      orgError,
    ],
  );

  return (
    <AppContext.Provider value={value}>
      {showLoadingScreen ? <LoadingScreen /> : children}
    </AppContext.Provider>
  );
}

export function useAppContext(): AppContextType {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useAppContext must be used within an AppContextProvider");
  }
  return context;
}

export { useCurrentOrganization, useUser };
